import { BaseModel } from '../../core/base/base.model';
import { UserModel } from '../models/user.model';
import { ISelectCategory, ISelectOption } from './select-option.interface';

export class Searchable<T extends BaseModel | UserModel> {
  loaded: boolean;
  loading: boolean;
  original: T[];
  options: ISelectOption[];
  categories: ISelectCategory[];

  private page: number = 0;
  totalItems: number = 0;
  private totalPages: number = 0;

  private originalMap: Map<number, T>;
  private optionMap: Map<number, ISelectOption>;
  private categoryMap: Map<number, ISelectCategory>;

  constructor() {
    this.loaded = false;
    this.loading = false;
    this.original = [];
    this.options = [];
    this.categories = [];

    this.originalMap = new Map<number, T>();
    this.optionMap = new Map<number, ISelectOption>();
    this.categoryMap = new Map<number, ISelectCategory>();
  }

  canFetch(page: number | string) {
    return !this.totalPages || Number(page) <= this.totalPages;
  }

  getOptionLabel(value: number): string {
    return this.optionMap.get(value)?.label || '';
  }

  getOriginalById(id: number): T | undefined {
    return this.originalMap.get(id);
  }

  getCategoryLabel(value: number): string {
    return this.categoryMap.get(value)?.label || '';
  }

  update(resp: any, page: number | string) {
    const newData: T[] = resp?.data || [];
    const newSelect: ISelectOption[] = resp?.select || [];

    if (Number(page) > 1) {
      for (const item of newData) {
        if (!this.originalMap.has(item.id!)) {
          this.originalMap.set(item.id!, item);
        }
      }

      for (const option of newSelect) {
        if (!this.optionMap.has(option.value)) {
          this.optionMap.set(option.value, option);
        }
      }
    } else {
      this.originalMap = new Map(newData.map((item: T) => [item.id!, item]));
      this.optionMap = new Map(newSelect.map((opt: ISelectOption) => [opt.value, opt]));
    }

    // To get the array for display
    this.original = Array.from(this.originalMap.values());
    this.options = Array.from(this.optionMap.values());

    this.updateCounts(resp);
  }

  updateCategories(resp: any, page: number | string) {
    const newData: T[] = resp?.data || [];
    const newSelect: ISelectCategory[] = resp?.select || [];

    if (Number(page) > 1) {
      for (const item of newData) {
        if (!this.originalMap.has(item.id!)) {
          this.originalMap.set(item.id!, item);
        }
      }

      for (const option of newSelect) {
        if (!this.categoryMap.has(option.value)) {
          this.categoryMap.set(option.value, option);
        }
      }
    } else {
      this.originalMap = new Map(newData.map((item: T) => [item.id!, item]));
      this.categoryMap = new Map(newSelect.map((opt: ISelectCategory) => [opt.value, opt]));
    }

    // To get the array for display
    this.original = Array.from(this.originalMap.values());
    this.categories = Array.from(this.categoryMap.values());

    this.updateCounts(resp);
  }

  updateCleanInstructions(resp: any, selectedIds?: number[]) {
    const newData: T[] = resp?.data || [];
    const newSelect: ISelectCategory[] = resp?.select || [];

    this.originalMap = new Map(newData.map((item: T) => [item.id!, item]));
    this.categoryMap = new Map(newSelect.map((opt: ISelectCategory) => [opt.value, opt]));

    // To get the array for display
    this.original = Array.from(this.originalMap.values());
    this.categories = Array.from(this.categoryMap.values());

    this.updateCounts(resp);
  }

  private updateCounts(resp: any) {
    this.page = Number(resp?.metadata?.currentPage) || 0;
    this.totalItems = resp?.metadata?.totalItems || 0;
    this.totalPages = resp?.metadata?.totalPages || 0;
    this.loading = false;
    this.loaded = true;
  }
}
